import { createFeaturedImageSlug } from './homepage-navigation';

// Shared by the public site and the admin pages, which run on the same origin. The admin pages read the same
// browser identity so that signing in can exclude this browser's public-site visits from the statistics.
export const browserIdentityStorageKey = 'digitaljedwin.telemetry.browser';
export const browserIdentityLifetimeMilliseconds = 90 * 24 * 60 * 60 * 1000;
const trackedPublicPaths = new Set(['/', '/about', '/subscribe', '/contact', '/rights', '/prints', '/return-policy', '/order/thanks']);
const identityIdPattern = /^[0-9a-f]{32}$/;

export type StoredBrowserIdentity = {
    id: string;
    expiresUtc: string;
};

export function isTrackedPublicPath(path: string): boolean {
    if (trackedPublicPaths.has(path)) return true;
    if (!path.toLowerCase().startsWith('/photo/') || path.includes('?') || path.includes('#')) return false;
    const slug = path.slice('/photo/'.length);
    if (slug.length === 0 || slug.includes('/')) return false;
    try {
        return createFeaturedImageSlug(slug) === slug;
    } catch (error) {
        if (error instanceof RangeError) return false;
        throw error;
    }
}

export function isIdentityId(value: unknown): value is string {
    return typeof value === 'string' && identityIdPattern.test(value);
}

/** Returns the ID from a stored browser identity when it is well formed and unexpired, otherwise null. */
export function readValidBrowserId(storedValue: string | null, now: number): string | null {
    if (storedValue == null) return null;
    try {
        const stored = JSON.parse(storedValue) as Partial<StoredBrowserIdentity> | null;
        if (stored == null
            || !isIdentityId(stored.id)
            || typeof stored.expiresUtc !== 'string'
            || !(Date.parse(stored.expiresUtc) > now)) return null;
        return stored.id;
    } catch {
        return null;
    }
}

export function createBrowserIdentity(now: number, id = createIdentityId()): StoredBrowserIdentity {
    return {
        id,
        expiresUtc: new Date(now + browserIdentityLifetimeMilliseconds).toISOString()
    };
}

/**
 * Returns the stored browser ID with its lifetime renewed, or stores and returns a new one. Returns null when
 * storage is unavailable and no stored ID could be read, so callers decide whether an unstored ID is useful.
 */
export function getOrCreateStoredBrowserId(
    getStorage: () => Storage = () => window.localStorage,
    now = Date.now()
): string | null {
    let storage: Storage;
    let storedId: string | null;
    try {
        storage = getStorage();
        storedId = readValidBrowserId(storage.getItem(browserIdentityStorageKey), now);
    } catch {
        return null;
    }

    const id = storedId ?? createIdentityId();
    try {
        storage.setItem(browserIdentityStorageKey, JSON.stringify(createBrowserIdentity(now, id)));
    } catch {
        return storedId;
    }
    return id;
}

export function createIdentityId(): string {
    return globalThis.crypto.randomUUID().replaceAll('-', '');
}

// Channels: where a visitor session came from. These mirror TelemetryChannels.Classify on the server, which
// classifies the same session from the hub query; the gallery keeps its own copy so it can send the channel with a
// checkout without sending any identifier.
export const sessionChannelStorageKey = 'digitaljedwin.telemetry.channel';
export const unknownChannel = 'unknown';
const campaignCodePattern = /^[a-z0-9]{6}$/;
const referrerChannels = new Set(['google', 'bing', 'pinterest', 'instagram', 'facebook', 'other', 'direct']);

export type StoredSessionChannel = {
    sessionId: string;
    channel: string;
    campaignCode: string | null;
    /** For the other channel only, the referring site's host (never its page), sent with a checkout. */
    referrerHost: string | null;
};

const referrerHostPattern = /^[a-z0-9._\-[\]:]{1,253}$/;

/** True for a plausible referring host as the browser reports it: lowercase host characters only, no path or credentials. */
export function isReferrerHost(value: unknown): value is string {
    return typeof value === 'string' && referrerHostPattern.test(value) && !value.includes('..');
}

/** The host of an http or https referrer, lowercase (an IDN as punycode, as the server stores it), or null. */
export function getReferrerHost(referrer: string): string | null {
    try {
        const url = new URL(referrer);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
        const host = url.hostname.toLowerCase();
        return isReferrerHost(host) ? host : null;
    } catch {
        return null;
    }
}

export function isCampaignCode(value: unknown): value is string {
    return typeof value === 'string' && campaignCodePattern.test(value);
}

/** True for the channel forms the server records: a referrer channel, unknown, or qr: with a well-formed code. */
export function isWellFormedChannel(value: unknown): value is string {
    return typeof value === 'string'
        && (value === unknownChannel
            || referrerChannels.has(value)
            || (value.startsWith('qr:') && isCampaignCode(value.slice(3))));
}

/** Classifies a referrer URL as the server does. A same-site or unreadable referrer counts as none (direct). */
export function classifyReferrerChannel(referrer: string, currentHostname: string): string {
    let host: string;
    try {
        const url = new URL(referrer);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return 'direct';
        host = url.hostname.toLowerCase().replace(/\.$/, '');
    } catch {
        return 'direct';
    }
    if (host.length === 0 || host === currentHostname.toLowerCase()) return 'direct';
    if (isBrandDomain(host, 'google')) return 'google';
    if (isDomainOrSubdomain(host, 'bing.com')) return 'bing';
    if (isBrandDomain(host, 'pinterest') || isDomainOrSubdomain(host, 'pin.it')) return 'pinterest';
    if (isDomainOrSubdomain(host, 'instagram.com')) return 'instagram';
    if (isDomainOrSubdomain(host, 'facebook.com')) return 'facebook';
    return 'other';
}

function isBrandDomain(host: string, brand: string): boolean {
    const labels = host.split('.');
    for (let index = labels.length - 3; index <= labels.length - 2; index++) {
        if (index < 0 || labels[index] !== brand) continue;
        if (labels.slice(index + 1).every(label => /^[a-z]{2,3}$/.test(label))) return true;
    }
    return false;
}

function isDomainOrSubdomain(host: string, domain: string): boolean {
    return host === domain || host.endsWith(`.${domain}`);
}

type CampaignLocation = { pathname: string; search: string; hash: string };
type CampaignHistory = { state: unknown; replaceState: (data: unknown, unused: string, url?: string) => void };

/**
 * Takes the campaign code a /q/ redirect put in the fragment (#c=<code>), once. The c parameter is removed from the
 * address with replaceState, keeping the history state and any other fragment parameters, so a reload, a shared
 * link or the gallery's own history handling never sees it again. Returns the code when it is well formed.
 */
export function takeCampaignCodeFromLocation(location: CampaignLocation, history: CampaignHistory): string | null {
    if (!location.hash.startsWith('#')) return null;
    const parameters = new URLSearchParams(location.hash.slice(1));
    if (!parameters.has('c')) return null;
    const code = parameters.get('c');
    parameters.delete('c');
    const remaining = parameters.toString();
    try {
        history.replaceState(history.state, '', `${location.pathname}${location.search}${remaining.length > 0 ? `#${remaining}` : ''}`);
    } catch {
        // The code is still used for this page even if the address cannot be tidied.
    }
    return isCampaignCode(code) ? code : null;
}

/**
 * Resolves this tab session's channel, as the server records it. A campaign code arriving now wins and is kept; a
 * session already classified keeps its channel; otherwise the landing referrer decides. Stored per session in
 * sessionStorage, tagged with the session ID so a new session never inherits an old channel.
 */
export function resolveSessionChannel(
    options: { sessionId: string; campaignCode: string | null; referrer: string; hostname: string },
    getStorage: () => Storage = () => window.sessionStorage
): StoredSessionChannel {
    let storage: Storage | null = null;
    let stored: StoredSessionChannel | null = null;
    try {
        storage = getStorage();
        stored = readStoredSessionChannel(storage.getItem(sessionChannelStorageKey), options.sessionId);
    } catch {
        storage = null;
    }

    const resolved: StoredSessionChannel = options.campaignCode != null
        ? { sessionId: options.sessionId, channel: `qr:${options.campaignCode}`, campaignCode: options.campaignCode, referrerHost: null }
        : stored ?? classifyLanding(options);
    try {
        storage?.setItem(sessionChannelStorageKey, JSON.stringify(resolved));
    } catch {
        // Without storage the channel still applies to this page.
    }
    return resolved;
}

function classifyLanding(options: { sessionId: string; referrer: string; hostname: string }): StoredSessionChannel {
    const channel = classifyReferrerChannel(options.referrer, options.hostname);
    return {
        sessionId: options.sessionId,
        channel,
        campaignCode: null,
        referrerHost: channel === 'other' ? getReferrerHost(options.referrer) : null
    };
}

function readStoredSessionChannel(value: string | null, sessionId: string): StoredSessionChannel | null {
    if (value == null) return null;
    try {
        const stored = JSON.parse(value) as Partial<StoredSessionChannel> | null;
        if (stored == null || stored.sessionId !== sessionId || !isWellFormedChannel(stored.channel)) return null;
        const campaignCode = isCampaignCode(stored.campaignCode) ? stored.campaignCode : null;
        if (stored.channel.startsWith('qr:') && stored.channel !== `qr:${campaignCode}`) return null;
        // A record from before hosts were kept, or a malformed host, keeps its channel without a host.
        const referrerHost = stored.channel === 'other' && isReferrerHost(stored.referrerHost) ? stored.referrerHost : null;
        return { sessionId, channel: stored.channel, campaignCode, referrerHost };
    } catch {
        return null;
    }
}
